import {
  EntityEnum,
  PCOVertebraLabels,
  intervertebralDisks,
  vertebraGroups,
  VertebralRegionsEnum,
} from "./../enums/AnnotationsEnum";
const SACRAL_LEVELS = ["S1", "S2"];
const SACRUM_TOKEN = "SACRUM";

const expandSacrumLevels = vertebrae =>
  (vertebrae || []).flatMap(level =>
    level === SACRUM_TOKEN ? SACRAL_LEVELS : level
  );

const collapseSacrumLevels = vertebrae => {
  if (!Array.isArray(vertebrae)) return [];
  const result = [];
  let hasSacralLevel = false;

  vertebrae.forEach(level => {
    if (SACRAL_LEVELS.includes(level)) {
      hasSacralLevel = true;
    } else {
      result.push(level);
    }
  });

  if (hasSacralLevel) {
    result.push(SACRUM_TOKEN);
  }

  return result;
};

export const vertebraUtils = {
  _normalizeSacrumLabel: vertebra => {
    if (!vertebra) return vertebra;
    return SACRAL_LEVELS.includes(vertebra) ? SACRUM_TOKEN : vertebra;
  },

  getCentroidEntity: vertebra => {
    if (!vertebra) return null;
    const normalized = vertebraUtils._normalizeSacrumLabel(vertebra);
    if (normalized === SACRUM_TOKEN) return EntityEnum.SACRUM_C;

    const key = `VERTEBRAE_${normalized}_C`;
    return EntityEnum[key];
  },

  getVertebraFromId: id => {
    const entityKey = Object.entries(EntityEnum).find(
      ([_, value]) => value === id
    )?.[0];
    if (!entityKey) return null;
    if (entityKey === "SACRUM_C") return SACRUM_TOKEN;

    const matches = entityKey.match(/VERTEBRAE_([A-Z][0-9]+)_/);
    return matches ? matches[1] : null;
  },

  getVertebraId: vertebra => {
    if (!vertebra) return null;
    const normalized = vertebraUtils._normalizeSacrumLabel(vertebra);
    if (normalized === SACRUM_TOKEN) return EntityEnum.SACRUM_C;

    const key = `VERTEBRAE_${normalized}_C`;
    return EntityEnum[key];
  },

  expandSacrumSelection: vertebraList => expandSacrumLevels(vertebraList),

  collapseSacrumSelection: vertebraList => collapseSacrumLevels(vertebraList),

  getSacralLevels: () => [...SACRAL_LEVELS],

  // Get all available vertebrae (including optional L6/T13 if present)
  getAvailableVertebrae: (hasL6 = false, hasT13 = false) => {
    const vertebrae = [
      ...vertebraGroups.CERVICAL,
      ...vertebraGroups.THORACIC,
      ...vertebraGroups.LUMBAR,
    ];

    if (hasL6) vertebrae.push("L6");
    if (hasT13) vertebrae.push("T13");

    return vertebrae.sort((a, b) => {
      // Sort by region (C -> T -> L) and then by number
      const getRegionWeight = v => ({ C: 1, T: 2, L: 3 }[v[0]]);
      const aWeight = getRegionWeight(a);
      const bWeight = getRegionWeight(b);

      if (aWeight === bWeight) {
        return parseInt(a.slice(1)) - parseInt(b.slice(1));
      }
      return aWeight - bWeight;
    });
  },

  getCageDiskItems: (hasL6 = false, hasT13 = false) => [
    ...(hasL6
      ? intervertebralDisks.LUMBARL6
      : intervertebralDisks.LUMBAR),
    ...(hasT13
      ? intervertebralDisks.THORACICL6
      : intervertebralDisks.THORACIC),
    ...vertebraGroups.CERVICAL,
  ],

  getGrade2PcoLabel: (vertebra, hasL6 = false, hasT13 = false) => {
    if (!vertebra || vertebra === "-") return vertebra || "";

    const orderedVertebrae = [
      ...vertebraUtils.getAvailableVertebrae(hasL6, hasT13),
      "S1",
    ];
    const vertebraIndex = orderedVertebrae.indexOf(vertebra);
    const caudalVertebra =
      vertebraIndex >= 0 ? orderedVertebrae[vertebraIndex + 1] : null;

    if (caudalVertebra) {
      return `${vertebra}/${caudalVertebra}`;
    }

    return PCOVertebraLabels[vertebra] || vertebraUtils.getVertebraLabel(vertebra);
  },

  // Get formatted label for display
  getVertebraLabel: vertebra => {
    if (!vertebra) return "";
    return `${vertebra[0]}${vertebra.slice(1)}`;
  },

  // Get vertebra group (LUMBAR, THORACIC, CERVICAL)
  getVertebraGroup: vertebra => {
    if (!vertebra) return null;
    const region = vertebra[0];
    switch (region) {
      case "L":
        return VertebralRegionsEnum.LUMBAR;
      case "T":
        return VertebralRegionsEnum.THORACIC;
      case "C":
        return VertebralRegionsEnum.CERVICAL;
      default:
        return null;
    }
  },
};
